import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FlashMessagesService } from 'flash-messages-angular';
import { Subject } from 'rxjs';
import { PropertiesService } from 'src/app/services/properties.service';
import { DataTableDirective } from 'angular-datatables';
import { DatatablesHelper, PromiseHelper } from 'src/app/helpers';
import { ClientProperty, StoredFile } from 'src/app/models';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ClientPropertyFilesUploadDialogComponent } from 'src/app/shared/dialogs/client-property-files-upload-dialog/client-property-files-upload-dialog.component';
import { ClientPropertyFilesUploadDialogData } from 'src/app/shared/dialogs/client-property-files-upload-dialog/client-property-files-upload-dialog.viewmodel';

@Component({
  selector: 'app-client-property-files',
  templateUrl: './client-property-files.component.html',
  styleUrls: ['./client-property-files.component.css']
})
export class ClientPropertyFilesComponent implements OnInit, OnDestroy {
  property!: ClientProperty;
  files: StoredFile[] = [];
  loaded = false;

  dtOptions: DataTables.Settings = {};

  // We use this trigger because fetching the properties can take a while,
  // thus we ensure the data is fetched before rendering
  dtTrigger: Subject<any> = new Subject<any>();

  @ViewChild(DataTableDirective, { static: false })
  dtElement!: DataTableDirective;

  constructor(
    private propertiesService: PropertiesService,
    private flashMessage: FlashMessagesService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initTable();
    this.loadTable();
  }

  ngOnDestroy(): void {
    // Do not forget to unsubscribe the event
    this.dtTrigger.unsubscribe();
  }

  private initTable(): void {
    this.dtOptions = {
      pagingType: 'full_numbers',
      pageLength: 50,
      // enables horizontal scrolling on datatable
      scrollX: true,

      order: [[4, 'desc']],

      // Declare the use of the extension in the dom parameter and give a class for the css (can't get the css to apply though so commented out)
      // dom: '<"table-buttons"lBfr>tip',
      // dom: 'lBfrtip',
      dom: 'Bfti',
    };

    // Configure the buttons
    Object.assign(this.dtOptions, {
      buttons: [
        'excel'
      ]
    });
  }

  private async loadTable() {
    this.property = this.propertiesService.currentProperty;

    if (!this.property) {
      this.router.navigate(['..'], { relativeTo: this.route });
      return;
    }

    await PromiseHelper.delay(1);

    this.files = (this.property.files || []).filter(file => file.uploaded);

    if (this.loaded) {
      // Manually rerender table after changes
      this.rerender();
    } else {
      this.loaded = true;

      // Calling the DT trigger to manually render the table
      this.dtTrigger.next();
    }
  }

  private async tryReloadTable() {
    await this.loadTable().catch(() => {});
  }

  private rerender(): void {
    DatatablesHelper.redrawTable(this.dtElement, this.dtTrigger);
  }

  addFilesDialog() {
    const dialogConfig = new MatDialogConfig();

    const data: ClientPropertyFilesUploadDialogData = {
      propertyID: this.property._id,
      userID:     this.property.userID,
    };

    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = data;

    const dialogRef = this.dialog.open(ClientPropertyFilesUploadDialogComponent, dialogConfig);

    dialogRef.afterClosed().toPromise().then(async (res: any) => {
      if (!res.uploaded) return;

      await this.reloadCurrentProperty();

      // Rerender manually
      this.tryReloadTable();

      this.cd.detectChanges();
    });
  }

  private async reloadCurrentProperty() {
    const res: any = await this.propertiesService.getSelfClientProperties({ getmoveinsurvey: true }).toPromise();
    this.propertiesService.properties = res.properties;
    this.propertiesService.clientProperties = true;
    this.propertiesService.reloadCurrentProperty();
  }

  deleteFile(file: StoredFile) {
    if (confirm(`Are you sure you want to permanently delete file ${file.filename}?`) !== true) {
      return;
    }

    // Delete File
    this.propertiesService.deleteClientPropertyFile(this.property._id, file._id).toPromise().then(async (data: any) => {

      if (!data.success) return;

      // await this.reloadCurrentProperty().catch(() => {});

      const index = this.property.files.indexOf(file);

      this.property.files.splice(index, 1);

      // Rerender manually
      this.tryReloadTable();

      // Display success
      this.flashMessage.show("File deleted.", {
        cssClass: 'alert-success',
        timeout: 3000
      });

      this.cd.detectChanges();
    });
  }

  sortDate(value: any) {
    return value ? +new Date(value) : 0;
  }
}
